import {
  FocalEventPosterImage,
  getCoverEventPosterImage,
} from '@/features/events/shared/FocalEventPosterImage';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import type { VenuePublicDeal } from '@/lib/api/apiTypes';
import { type DiscoveryEvent } from '@/lib/api/apiTypes';
import { formatEventDayLabel, formatEventTime } from '@/features/events/eventFormatting';
import { formatDealDays, formatDealTimeRange, getDealTiming } from '@/features/venues/deals/deals';
import { cn } from '@/lib/utils';
import {
  ArrowRightIcon,
  CalendarDaysIcon,
  Clock3Icon,
  TicketPercentIcon,
} from 'lucide-react-native';
import * as React from 'react';
import { Pressable, View } from 'react-native';

export function VenueActivitySpotlight({
  deals,
  events,
  highlightedDealID,
  onOpenEvent,
}: {
  deals: VenuePublicDeal[];
  events: DiscoveryEvent[];
  highlightedDealID?: string | null;
  onOpenEvent: (eventID: string) => void;
}) {
  const highlightedDeal = React.useMemo(() => {
    if (highlightedDealID) {
      const selectedDeal = deals.find((deal) => deal.id === highlightedDealID);
      if (selectedDeal) {
        return selectedDeal;
      }
    }

    return (
      deals.find((deal) => getDealTiming(deal).state === 'live') ??
      deals.find((deal) => getDealTiming(deal).state === 'scheduled') ??
      null
    );
  }, [deals, highlightedDealID]);
  const nextEvent = events[0] ?? null;

  if (!highlightedDeal && !nextEvent) {
    return null;
  }

  return (
    <View className="mx-4 mt-5 gap-2.5">
      {highlightedDeal ? (
        <SpotlightDealRow
          deal={highlightedDeal}
          isSelected={highlightedDeal.id === highlightedDealID}
        />
      ) : null}

      {nextEvent ? (
        <SpotlightEventRow
          event={nextEvent}
          hasLeadDeal={Boolean(highlightedDeal)}
          onOpenEvent={onOpenEvent}
        />
      ) : null}
    </View>
  );
}

function SpotlightDealRow({
  deal,
  isSelected,
}: {
  deal: VenuePublicDeal;
  isSelected: boolean;
}) {
  const timing = getDealTiming(deal);
  const isLive = timing.state === 'live';

  return (
    <View className="flex-row items-center gap-3 overflow-hidden rounded-2xl border border-border/50 bg-card/60 px-3.5 py-3">
      <View
        className={cn(
          'size-10 items-center justify-center rounded-xl',
          isLive ? 'bg-primary' : 'bg-muted'
        )}>
        <Icon
          as={TicketPercentIcon}
          size={18}
          className={isLive ? 'text-primary-foreground' : 'text-muted-foreground'}
        />
      </View>
      <View className="min-w-0 flex-1 gap-0.5">
        <View className="flex-row items-center gap-2">
          <Text
            variant="small"
            className={cn(
              'font-semibold uppercase tracking-[1.2px]',
              isLive ? 'text-primary' : 'text-muted-foreground'
            )}>
            {isLive ? 'Live now' : timing.label}
          </Text>
          {isSelected ? (
            <Text variant="small" className="text-muted-foreground/60">
              · Deal match
            </Text>
          ) : null}
        </View>
        <Text
          className="text-[15px] font-semibold leading-5 text-foreground"
          numberOfLines={1}>
          {deal.title}
        </Text>
        <View className="mt-0.5 flex-row items-center gap-1.5">
          <Icon as={Clock3Icon} size={12} className="text-muted-foreground" />
          <Text variant="small" className="text-muted-foreground">
            {formatDealTimeRange(deal)} · {formatDealDays(deal.active_days)}
          </Text>
        </View>
      </View>
    </View>
  );
}

function SpotlightEventRow({
  event,
  hasLeadDeal,
  onOpenEvent,
}: {
  event: DiscoveryEvent;
  hasLeadDeal: boolean;
  onOpenEvent: (eventID: string) => void;
}) {
  return (
    <Pressable
      className="flex-row items-center gap-3 overflow-hidden rounded-2xl border border-border/50 bg-card/60 px-3.5 py-3"
      onPress={() => onOpenEvent(event.id)}>
      <SpotlightEventPoster event={event} />
      <View className="min-w-0 flex-1 gap-0.5">
        <Text variant="small" className="font-medium text-muted-foreground">
          {hasLeadDeal ? 'Next event' : 'Upcoming'}
        </Text>
        <Text
          className="text-[15px] font-semibold leading-5 text-foreground"
          numberOfLines={2}>
          {event.title}
        </Text>
        <Text variant="small" className="mt-0.5 text-muted-foreground">
          {formatEventDayLabel(event.starts_at)} · {formatEventTime(event.starts_at)}
        </Text>
      </View>
      <Icon as={ArrowRightIcon} size={16} className="text-muted-foreground" />
    </Pressable>
  );
}

function SpotlightEventPoster({ event }: { event: DiscoveryEvent }) {
  const [failedPosterURL, setFailedPosterURL] = React.useState<string | null>(null);
  const posterImage = React.useMemo(() => getCoverEventPosterImage(event), [event]);
  const posterURL = posterImage?.public_url ?? null;
  const showPoster = Boolean(posterURL) && failedPosterURL !== posterURL;

  return (
    <View className="size-[52px] overflow-hidden rounded-xl bg-muted">
      {showPoster && posterImage ? (
        <FocalEventPosterImage
          image={posterImage}
          className="h-full w-full"
          onError={() => setFailedPosterURL(posterURL)}
        />
      ) : (
        <View className="h-full w-full items-center justify-center bg-primary/12">
          <Icon as={CalendarDaysIcon} size={20} className="text-primary" />
        </View>
      )}
    </View>
  );
}
